import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  Mail,
  Phone,
  Globe2,
  ChevronRight,
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { partners, certifications } from '../data/mock';
import { useT } from '../i18n/LanguageContext';

/* =========================================================
   OFFICES
   IMPORTANT:
   France first
   Algeria second
   Saudi Arabia third
========================================================= */

const offices = [
  {
    id: 'france',

    city: {
      fr: 'Vitrolles',
      en: 'Vitrolles',
      ar: 'فيترول',
    },

    country: {
      fr: 'France',
      en: 'France',
      ar: 'فرنسا',
    },

    flag: '🇫🇷',

    role: {
      fr: 'Agence France',
      en: 'France Office',
      ar: 'فرع فرنسا',
    },

    address: {
      fr: "92 Boulevard de l'Europe, 13127 Vitrolles",
      en: "92 Boulevard de l'Europe, 13127 Vitrolles",
      ar: '92 شارع أوروبا، 13127 فيترول، فرنسا',
    },

    phone: '+33 4 42 07 22 62',

    email: 'contact@gpefrance.eu',

    website: 'www.gpefrance.eu',

    coordinates: [43.4547, 5.2483],
  },

  {
    id: 'algeria',

    city: {
      fr: 'Bir Mourad Raïs',
      en: 'Bir Mourad Raïs',
      ar: 'بئر مراد رايس',
    },

    country: {
      fr: 'Algérie',
      en: 'Algeria',
      ar: 'الجزائر',
    },

    flag: '🇩🇿',

    role: {
      fr: 'Siège social',
      en: 'Head Office',
      ar: 'المقر الرئيسي',
    },

    address: {
      fr: 'Chemin Sidi Yahia, Résidence Coloris, Bir Mourad Raïs 16005, Alger',
      en: 'Chemin Sidi Yahia, Résidence Coloris, Bir Mourad Raïs 16005, Algiers',
      ar: 'طريق سيدي يحيى، إقامة كولوريس، بئر مراد رايس 16005، الجزائر',
    },

    phone: '+213 549 48 00 97',

    email: 'contact@gpealgerie.com',

    website: 'gpealgerie.com',

    coordinates: [36.7478, 3.0362],
  },

  {
    id: 'saudi',

    city: {
      fr: 'Riyadh',
      en: 'Riyadh',
      ar: 'الرياض',
    },

    country: {
      fr: 'Arabie Saoudite',
      en: 'Saudi Arabia',
      ar: 'المملكة العربية السعودية',
    },

    flag: '🇸🇦',

    role: {
      fr: 'Bureau régional',
      en: 'Regional Office',
      ar: 'المكتب الإقليمي',
    },

    address: {
      fr: 'King Fahd District, Riyadh',
      en: 'King Fahd District, Riyadh',
      ar: 'حي الملك فهد، الرياض',
    },

    phone: '+966 50 859 6539',

    email: 'contact@gpe-gulf.com',

    website: 'gpe-gulf.com',

    coordinates: [24.7136, 46.6753],
  },
];

/* =========================================================
   PAGE TRANSLATIONS
========================================================= */

const translations = {
  fr: {
    badge: 'IMPLANTATIONS',
    title: '3 bureaux sur 3 continents',

    officesTitleA: 'Une présence sur',
    officesTitleAccent: '3 continents',

    officesSub:
      'Nos équipes opèrent depuis la France, l’Algérie et l’Arabie Saoudite pour accompagner nos clients en Europe, en Afrique et dans le Golfe.',

    officesHeadingBadge: 'NOS IMPLANTATIONS',
    officesHeading: 'Une présence sur 3 continents.',

    partnersBadge: '+ PARTENAIRES INTERNATIONAUX',

    partnersTitleA: 'Des alliances',
    partnersTitleAccent: 'stratégiques.',

    partnersSub:
      'Des partenaires de confiance nous accompagnent dans la réalisation de projets énergétiques et techniques majeurs.',

    certificationsBadge: 'CERTIFICATIONS & QUALIFICATIONS',

    certificationsTitleA: 'Certifiés. Reconnus.',
    certificationsTitleAccent: 'Fiables.',

    address: 'Adresse',
    phone: 'Téléphone',
    email: 'Email',
    website: 'Site web',

    contact: 'Nous contacter',

    ctaTitle: 'Un projet international ?',

    ctaSub:
      'Contactez le bureau le plus proche pour étudier votre projet, quel que soit le continent.',
    heroBadge: 'PRÉSENCE MONDIALE',
    heroTitleA: 'Au-delà',
    heroTitleAccent: 'des frontières.',
    heroDescription: 'GPE Énergies & Services accompagne des projets d’envergure en France et à l’international, en apportant son expertise technique en électricité et énergies renouvelables sur chaque continent.',
    heroStats: [
      ['3', 'CONTINENTS'],
      ['3+', 'PAYS COUVERTS'],
      ['150+', 'PROJETS LIVRÉS'],
      ['10+', 'PARTENAIRES INTERNATIONAUX'],
    ],
  },

  en: {
    badge: 'LOCATIONS',
    title: '3 offices across 3 continents',

    officesTitleA: 'A presence across',
    officesTitleAccent: '3 continents',

    officesSub:
      'Our teams operate from France, Algeria and Saudi Arabia to support clients across Europe, Africa and the Gulf.',

    officesHeadingBadge: 'OUR LOCATIONS',
    officesHeading: 'A presence across 3 continents.',

    partnersBadge: '+ INTERNATIONAL PARTNERS',

    partnersTitleA: 'Strategic',
    partnersTitleAccent: 'alliances.',

    partnersSub:
      'Trusted partners support us in delivering major energy and technical projects.',

    certificationsBadge: 'CERTIFICATIONS & QUALIFICATIONS',

    certificationsTitleA: 'Certified. Recognised.',
    certificationsTitleAccent: 'Reliable.',

    address: 'Address',
    phone: 'Phone',
    email: 'Email',
    website: 'Website',

    contact: 'Contact us',

    ctaTitle: 'An international project?',

    ctaSub:
      'Contact the nearest office to study your project, whatever the continent.',
    heroBadge: 'GLOBAL PRESENCE',
    heroTitleA: 'Beyond',
    heroTitleAccent: 'borders.',
    heroDescription: 'GPE Énergies & Services supports major projects in France and internationally, bringing its technical expertise in electricity and renewable energy across every continent.',
    heroStats: [
      ['3', 'CONTINENTS'],
      ['3+', 'COUNTRIES COVERED'],
      ['150+', 'PROJECTS DELIVERED'],
      ['10+', 'INTERNATIONAL PARTNERS'],
    ],
  },

  ar: {
    badge: 'مواقعنا',
    title: '3 مكاتب في 3 قارات',

    officesTitleA: 'حضور في',
    officesTitleAccent: '3 قارات',

    officesSub:
      'تعمل فرقنا من فرنسا والجزائر والمملكة العربية السعودية لمرافقة عملائنا في أوروبا وأفريقيا ومنطقة الخليج.',

    officesHeadingBadge: 'مواقعنا',
    officesHeading: 'حضور في 3 قارات.',

    partnersBadge: '+ شركاؤنا الدوليون',

    partnersTitleA: 'تحالفات',
    partnersTitleAccent: 'استراتيجية.',

    partnersSub:
      'نتعاون مع شركاء موثوقين لتنفيذ مشاريع الطاقة والمشاريع التقنية الكبرى.',

    certificationsBadge: 'الشهادات والمؤهلات',

    certificationsTitleA: 'معتمدون. موثوقون.',
    certificationsTitleAccent: 'يمكن الاعتماد علينا.',

    address: 'العنوان',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    website: 'الموقع الإلكتروني',

    contact: 'اتصل بنا',

    ctaTitle: 'لديكم مشروع دولي؟',

    ctaSub:
      'تواصلوا مع أقرب مكتب لدراسة مشروعكم، أينما كان موقعه.',
    heroBadge: 'حضور عالمي',
    heroTitleA: 'ما وراء',
    heroTitleAccent: 'الحدود.',
    heroDescription: 'ترافق GPE Énergies & Services المشاريع الكبرى في فرنسا وعلى المستوى الدولي، من خلال خبرتها التقنية في الكهرباء والطاقة المتجددة في مختلف القارات.',
    heroStats: [
      ['3', 'قارات'],
      ['3+', 'دول مغطاة'],
      ['150+', 'مشروعًا منجزًا'],
      ['10+', 'شركاء دوليين'],
    ],
  },
};

/* =========================================================
   LEAFLET MARKER
   Smaller than previous version
========================================================= */

const createMarkerIcon = (active = false) => {
  const size = active ? 34 : 22;
  const dot = active ? 20 : 11;
  const border = active ? 4 : 2;

  return L.divIcon({
    className: 'gpe-map-marker',

    html: `
      <div style="
        position:relative;
        width:${size}px;
        height:${size}px;
      ">

        ${
          active
            ? `
              <div style="
                position:absolute;
                inset:-9px;
                border-radius:50%;
                background:rgba(164,235,39,.16);
                box-shadow:0 0 20px rgba(164,235,39,.20);
              "></div>
            `
            : ''
        }

        <div style="
          position:absolute;
          left:50%;
          top:50%;
          transform:translate(-50%,-50%);
          width:${dot}px;
          height:${dot}px;
          border-radius:50%;
          background:#a4eb27;
          border:${border}px solid #ffffff;
          box-shadow:0 2px 8px rgba(0,0,0,.18);
        "></div>

      </div>
    `,

    iconSize: [size, size],

    iconAnchor: [
      size / 2,
      size / 2,
    ],
  });
};

/* =========================================================
   COMPONENT
========================================================= */

const International = () => {
  const { lang } = useT();

  const currentLang =
    lang === 'ar' || lang === 'en' || lang === 'fr'
      ? lang
      : 'fr';

  const t = translations[currentLang];

  const isArabic = currentLang === 'ar';

  /* France selected first */
  const [selectedId, setSelectedId] = useState('france');

  const mapRef = useRef(null);

  const mapContainerRef = useRef(null);

  const markersRef = useRef({});

  const selectedOffice =
    offices.find(
      (office) => office.id === selectedId
    ) || offices[0];

  /* =========================================================
     SCROLL TOP
  ========================================================= */

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* =========================================================
     CREATE MAP
  ========================================================= */

  useEffect(() => {
    if (
      !mapContainerRef.current ||
      mapRef.current
    ) {
      return;
    }

    /* France is initial map */
    const initialOffice = offices[0];

    const map = L.map(
      mapContainerRef.current,
      {
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: true,
        dragging: true,
        doubleClickZoom: true,
        touchZoom: true,
      }
    ).setView(
      initialOffice.coordinates,
      11
    );

    /* OPENSTREETMAP */

    L.tileLayer(
      'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 20,

        attribution:
          '&copy; OpenStreetMap contributors',
      }
    ).addTo(map);

    /* MARKERS */

    offices.forEach((office) => {
      const marker = L.marker(
        office.coordinates,
        {
          icon: createMarkerIcon(
            office.id === 'france'
          ),
        }
      ).addTo(map);

      marker.on(
        'click',
        () => {
          setSelectedId(office.id);
        }
      );

      markersRef.current[
        office.id
      ] = marker;
    });

    mapRef.current = map;

    /* Fix Leaflet dimensions */

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 300);

    return () => {
      map.remove();

      mapRef.current = null;

      markersRef.current = {};
    };
  }, []);

  /* =========================================================
     CHANGE SELECTED OFFICE
  ========================================================= */

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;

    map.flyTo(
      selectedOffice.coordinates,
      11,
      {
        duration: 1.1,
      }
    );

    offices.forEach((office) => {
      const marker =
        markersRef.current[
          office.id
        ];

      if (!marker) {
        return;
      }

      marker.setIcon(
        createMarkerIcon(
          office.id ===
            selectedOffice.id
        )
      );
    });

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 500);
  }, [selectedOffice]);

  /* =========================================================
     LANGUAGE / LAYOUT CHANGE
  ========================================================= */

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 200);
  }, [currentLang]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <main
      className="bg-white gpe-international-page"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <style>{`
        .gpe-international-page .leaflet-control-zoom {
          transform: scale(.82);
          transform-origin: top left;
        }
        .gpe-international-page .leaflet-control-attribution {
          font-size: 10px;
          line-height: 14px;
        }
      `}</style>

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">

        {/* SAME HERO BACKGROUND DESIGN AS REFERENCES */}

        <img
          src="https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3ODQ4MTY4NDJ8MA&ixlib=rb-4.1.0&q=85"
          alt="International presence"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        {/* HERO CONTENT */}

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          {/* BADGE */}

          <span
            className="
              inline-flex
              items-center
              px-4
              py-2
              rounded-full
              border
              border-[#f5c518]/50
              bg-[#f5c518]/10
              text-[#f5c518]
              text-[11px]
              tracking-[0.25em]
              font-bold
              uppercase
            "
          >
            {t.heroBadge}
          </span>

          {/* TITLE */}

          <h1
            className="
              mt-8
              max-w-6xl
              text-white
              text-5xl
              md:text-6xl
              lg:text-7xl
              font-black
              leading-[0.98]
              tracking-tight
            "
          >
            {t.heroTitleA}{' '}

            <span className="font-serif italic text-[#f5c518]">
              {t.heroTitleAccent}
            </span>
          </h1>

          {/* DESCRIPTION */}

          <p
            className="
              mt-7
              max-w-3xl
              text-lg
              md:text-xl
              text-white/80
              leading-relaxed
            "
          >
            {t.heroDescription}
          </p>

          {/* STATS */}

          <div
            className="
              mt-12
              grid
              grid-cols-2
              lg:grid-cols-4
              gap-4
            "
          >

            {t.heroStats.map(([value, label]) => (

              <div
                key={label}
                className="
                  rounded-2xl
                  border
                  border-white/15
                  bg-white/[0.08]
                  backdrop-blur-sm
                  px-6
                  py-6
                "
              >

                <div className="text-4xl font-black text-[#f5c518]">
                  {value}
                </div>

                <div className="mt-2 text-[11px] tracking-[0.22em] font-bold text-white/65">
                  {label}
                </div>

              </div>

            ))}

          </div>

          {/* BREADCRUMB */}

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >
            <Link to="/" className="hover:text-white transition-colors">
              {currentLang === 'fr' ? 'Accueil' : currentLang === 'en' ? 'Home' : 'الرئيسية'}
            </Link>

            <ChevronRight className="w-3.5 h-3.5" />

            <span className="text-[#f5c518] font-semibold">
              International
            </span>
          </nav>

        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      </section>

      {/* =====================================================
          OFFICES INTRO
      ===================================================== */}

      <section className="pt-20 pb-10 md:pt-24 md:pb-10 bg-white">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="inline-flex items-center px-4 py-2 rounded-full border border-[#d5e5dc] bg-[#f3f7f4] text-[#29483b] text-[12px] tracking-[0.12em] font-bold uppercase">
            {t.officesHeadingBadge}
          </div>

          <h2 className="mt-8 text-[#193326] text-5xl md:text-6xl lg:text-[50px] font-black leading-[1] tracking-[-0.035em]">
            {t.officesHeading}
          </h2>

        </div>

      </section>

      {/* =====================================================
          OFFICES + MAP
      ===================================================== */}

      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

        <div className="max-w-[1750px] mx-auto px-2 md:px-3 lg:px-4">

          <div
            className="
              overflow-hidden
              rounded-[28px]
              border
              border-slate-200
              bg-white
              shadow-[0_15px_60px_-25px_rgba(15,23,42,.25)]
            "
          >

            <div className="grid lg:grid-cols-[400px_minmax(0,1fr)]">

              {/* =================================================
                  LEFT PANEL
              ================================================= */}

              <aside className="bg-white flex flex-col">

                {/* HEADER */}

                <div className="px-6 py-5 border-b border-slate-200">

                  <div className="flex items-center gap-3">

                    <span className="w-3 h-3 rounded-full bg-[#315c4a] shrink-0" />

                    <span className="text-[12px] tracking-[0.2em] font-bold text-slate-400">
                      {t.badge}
                    </span>

                  </div>

                  <h3 className="mt-3 text-[18px] font-black text-[#343434]">
                    {t.title}
                  </h3>

                </div>

                {/* OFFICE LIST */}

                <div className="shrink-0">

                  {offices.map((office) => {

                    const active =
                      office.id ===
                      selectedId;

                    return (

                      <button
                        key={office.id}
                        type="button"
                        onClick={() =>
                          setSelectedId(
                            office.id
                          )
                        }
                        className={`
                          w-full
                          text-left
                          px-6
                          py-4
                          flex
                          items-center
                          justify-between
                          gap-3
                          border-b
                          border-slate-200
                          transition-all
                          duration-200
                          ${
                            active
                              ? 'bg-slate-50 ring-2 ring-inset ring-[#0066d6]'
                              : 'bg-white hover:bg-slate-50'
                          }
                        `}
                      >

                        <div className="flex items-center gap-3 min-w-0">

                          <span className="text-[23px] shrink-0">
                            {office.flag}
                          </span>

                          <div className="min-w-0">

                            <div
                              className={`
                                text-[17px]
                                font-bold
                                truncate
                                ${
                                  active
                                    ? 'text-[#315c4a]'
                                    : 'text-[#303030]'
                                }
                              `}
                            >
                              {office.city[currentLang]}
                            </div>

                            <div className="mt-1 text-[14px] text-slate-400">
                              {office.country[currentLang]}
                            </div>

                          </div>

                        </div>

                        <span
                          className={`
                            w-2
                            h-2
                            rounded-full
                            shrink-0
                            ${
                              active
                                ? 'bg-[#315c4a]'
                                : 'bg-slate-200'
                            }
                          `}
                        />

                      </button>

                    );

                  })}

                </div>

                {/* DETAILS */}

                <div className="flex-1 px-6 py-5">

                  {/* ROLE */}

                  <div
                    className="
                      inline-flex
                      items-center
                      gap-2
                      px-3
                      py-2
                      rounded-full
                      border
                      border-slate-300
                      text-[#315c4a]
                      text-[11px]
                      tracking-[0.12em]
                      font-bold
                      uppercase
                    "
                  >

                    <span className="w-1.5 h-1.5 rounded-full bg-[#315c4a]" />

                    {officeRole(
                      selectedOffice,
                      currentLang
                    )}

                  </div>

                  {/* ADDRESS */}

                  <div className="mt-5 flex items-start gap-3">

                    <MapPin className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />

                    <div className="text-[14px] text-slate-500 leading-relaxed">
                      {selectedOffice.address[currentLang]}
                    </div>

                  </div>

                  {/* PHONE */}

                  <div className="mt-4 flex items-center gap-3">

                    <Phone className="w-5 h-5 text-slate-400 shrink-0" />

                    <a
                      href={`tel:${selectedOffice.phone.replace(/\s/g, '')}`}
                      className="font-bold text-[14px] text-[#343434] hover:text-[#0b3b8c]"
                      dir="ltr"
                    >
                      {selectedOffice.phone}
                    </a>

                  </div>

                  {/* EMAIL */}

                  <div className="mt-4 flex items-center gap-3">

                    <Mail className="w-5 h-5 text-slate-400 shrink-0" />

                    <a
                      href={`mailto:${selectedOffice.email}`}
                      className="font-bold text-[14px] text-[#343434] hover:text-[#0b3b8c] break-all"
                      dir="ltr"
                    >
                      {selectedOffice.email}
                    </a>

                  </div>

                  {/* WEBSITE */}

                  <div className="mt-4 flex items-center gap-3">

                    <Globe2 className="w-5 h-5 text-slate-400 shrink-0" />

                    <span
                      className="font-bold text-[14px] text-[#343434]"
                      dir="ltr"
                    >
                      {selectedOffice.website}
                    </span>

                  </div>

                  {/* COORDINATES */}

                  <div className="mt-6 pt-4 border-t border-slate-100 text-center">

                    <span
                      className="text-slate-300 text-[12px]"
                      dir="ltr"
                    >
                      {selectedOffice.coordinates[0].toFixed(4)}
                      {'° , '}
                      {selectedOffice.coordinates[1].toFixed(4)}
                      °
                    </span>

                  </div>

                </div>

              </aside>

              {/* =================================================
                  MAP
              ================================================= */}

              <div className="relative h-[400px] md:h-[460px] lg:h-[540px]">

                {/* FLOATING LABEL */}

                <div
                  className="
                    absolute
                    z-[1000]
                    top-5
                    left-5
                    md:left-6
                    bg-white
                    rounded-2xl
                    px-5
                    py-3
                    shadow-[0_10px_30px_-10px_rgba(0,0,0,.2)]
                    min-w-[175px]
                  "
                >

                  <div className="flex items-center gap-3">

                    <span className="text-[20px]">
                      {selectedOffice.flag}
                    </span>

                    <div>

                      <div className="font-black text-[#263b34] text-[17px]">
                        {selectedOffice.city[currentLang]}
                      </div>

                      <div className="text-slate-400 text-[13px]">
                        {selectedOffice.country[currentLang]}
                      </div>

                    </div>

                  </div>

                </div>

                {/* MAP */}

                <div
                  ref={mapContainerRef}
                  className="absolute inset-0 z-0"
                />

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          INTERNATIONAL PARTNERS / CLIENTS
      ===================================================== */}

      <section className="py-20 md:py-24 bg-[#f7f8fa]">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          {/* HEADER */}

          <div className="text-center">

            <div
              className="
                inline-flex
                items-center
                gap-2
                px-4
                py-2
                rounded-full
                border
                border-slate-200
                bg-white
                text-slate-500
                text-[11px]
                tracking-[0.22em]
                font-bold
                uppercase
              "
            >
              <span className="text-[#315c4a]">
                +
              </span>

              {t.partnersBadge}

            </div>

            <h2
              className="
                mt-5
                text-4xl
                md:text-5xl
                font-black
                text-[#0b1c3a]
              "
            >

              {t.partnersTitleA}{' '}

              <span className="font-serif italic text-[#17469c]">
                {t.partnersTitleAccent}
              </span>

            </h2>

            <p className="mt-5 max-w-2xl mx-auto text-slate-500 text-lg">
              {t.partnersSub}
            </p>

          </div>

          {/* LOGOS */}

          <div
            className="
              mt-12
              grid
              grid-cols-2
              md:grid-cols-3
              lg:grid-cols-5
              gap-4
            "
          >

            {partners
              .slice(0, 15)
              .map((partner, index) => (

                <div
                  key={`${partner.name}-${index}`}
                  className="
                    h-[135px]
                    bg-white
                    rounded-2xl
                    border
                    border-slate-200
                    flex
                    items-center
                    justify-center
                    p-5
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:shadow-lg
                  "
                >

                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="
                      max-w-[170px]
                      max-h-[72px]
                      w-auto
                      h-auto
                      object-contain
                    "
                    loading="lazy"
                  />

                </div>

              ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          CERTIFICATIONS
      ===================================================== */}

      <section className="py-20 md:py-24 bg-white">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          {/* HEADER */}

          <div className="text-center">

            <div
              className="
                inline-flex
                items-center
                px-4
                py-2
                rounded-full
                border
                border-slate-200
                bg-white
                text-slate-500
                text-[11px]
                tracking-[0.22em]
                font-bold
                uppercase
              "
            >
              {t.certificationsBadge}
            </div>

            <h2
              className="
                mt-5
                text-4xl
                md:text-5xl
                font-black
                text-[#0b1c3a]
              "
            >

              {t.certificationsTitleA}{' '}

              <span className="font-serif italic text-[#17469c]">
                {t.certificationsTitleAccent}
              </span>

            </h2>

          </div>

          {/* CERTIFICATION CARDS */}

          <div
            className="
              mt-12
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-4
              gap-5
            "
          >

            {certifications.map(
              (cert, index) => (

                <div
                  key={`${cert.name}-${index}`}
                  className="
                    h-[220px]
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    flex
                    flex-col
                    items-center
                    justify-between
                    p-7
                    shadow-[0_10px_35px_-25px_rgba(15,23,42,.3)]
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:shadow-lg
                  "
                >

                  <div className="flex-1 w-full flex items-center justify-center">

                    <img
                      src={cert.logo}
                      alt={cert.name}
                      className="
                        max-w-[210px]
                        max-h-[110px]
                        w-auto
                        h-auto
                        object-contain
                      "
                      loading="lazy"
                    />

                  </div>

                  <div className="w-full">

                    <div className="h-1 rounded-full bg-[#17469c]" />

                    <div className="mt-3 text-center">

                      <div className="font-bold text-[#0b1c3a] text-sm">
                        {cert.name}
                      </div>

                      <div className="mt-1 text-xs text-slate-400">
                        {cert.desc}
                      </div>

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="pb-20 pt-4">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div
            className="
              rounded-3xl
              bg-[#0b1c3a]
              px-8
              py-12
              md:px-14
              md:py-14
              text-white
              flex
              flex-wrap
              items-center
              justify-between
              gap-8
            "
          >

            <div>

              <h2 className="text-2xl md:text-3xl font-black">
                {t.ctaTitle}
              </h2>

              <p className="mt-3 text-white/70 max-w-2xl">
                {t.ctaSub}
              </p>

            </div>

            <Link
              to="/contact"
              className="
                inline-flex
                items-center
                gap-2
                px-7
                py-3.5
                rounded-full
                bg-[#f5c518]
                text-[#0b1c3a]
                font-bold
                hover:bg-yellow-300
                transition-colors
              "
            >

              {t.contact}

              <ArrowRight className="w-4 h-4" />

            </Link>

          </div>

        </div>

      </section>

    </main>
  );
};

/* =========================================================
   ROLE HELPER
========================================================= */

const officeRole = (office, lang) => {
  return office.role[lang];
};

export default International;